import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Bot,
  Menu,
  MessageSquare,
  Settings,
  Save,
  Plus,
  Trash2,
  Edit,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  AlertTriangle,
  Check,
  Copy,
  History,
  RotateCcw,
  Loader2,
  ArrowLeft,
  GripVertical,
  Link,
  Zap,
  MessageCircle,
  X,
  Upload,
  FileText,
  Image,
  FolderOpen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente para mostrar árbol de menús
function MenuTreeItem({ menu, allMenus, onEdit, onDelete, level = 0 }) {
  const [expanded, setExpanded] = useState(level < 2);
  const childMenus = allMenus.filter(m => m.parent_id === menu.id);
  
  return (
    <div className={`${level > 0 ? 'border-l-2 border-blue-200 ml-2 md:ml-4' : ''}`}>
      <div 
        className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 md:p-3 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors ${level === 0 ? 'bg-blue-50/50' : ''}`}
      >
        <div 
          className="flex items-center gap-2 flex-1 min-w-0"
          onClick={() => setExpanded(!expanded)}
        >
          {childMenus.length > 0 ? (
            expanded ? <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-blue-500 shrink-0" /> : <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-blue-500 shrink-0" />
          ) : (
            <div className="w-4 md:w-5 shrink-0" />
          )}
          <Menu className="h-4 w-4 md:h-5 md:w-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-gray-800 text-sm md:text-base block truncate">{menu.name}</span>
            <p className="text-xs text-gray-500 truncate" title={menu.title}>
              {menu.title?.replace(/\*/g, '').substring(0, 40)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pl-6 sm:pl-0">
          <Badge variant="outline" className="text-[10px] md:text-xs bg-white shrink-0">{menu.options?.length || 0} opc</Badge>
          <Button 
            variant="outline" 
            size="sm" 
            className="bg-white hover:bg-blue-100 border-blue-200 text-xs h-8"
            onClick={(e) => { e.stopPropagation(); onEdit(menu); }}
            data-testid={`edit-menu-${menu.id}`}
          >
            <Edit className="h-3 w-3 md:h-4 md:w-4 md:mr-1" />
            <span className="hidden md:inline">Editar</span>
          </Button>
          {menu.id !== 'menu_principal' && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-500 hover:bg-red-50 h-8"
              onClick={(e) => { e.stopPropagation(); onDelete(menu.id); }}
            >
              <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          )}
        </div>
      </div>
      {expanded && childMenus.length > 0 && (
        <div className="mt-1">
          {childMenus.map(child => (
            <MenuTreeItem 
              key={child.id} 
              menu={child} 
              allMenus={allMenus} 
              onEdit={onEdit} 
              onDelete={onDelete}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Editor de opciones de menú
function OptionEditor({ option, index, allMenus, messages, availableFunctions = [], onChange, onRemove, onEditMessage }) {
  const actionTypes = [
    { value: 'submenu', label: 'Ir a submenú', icon: Menu },
    { value: 'message', label: 'Mostrar mensaje(s)', icon: MessageSquare },
    { value: 'takeover', label: 'Takeover humano', icon: MessageCircle },
  ];

  // Combinar mensajes normales con funciones disponibles
  const allMessages = [
    ...availableFunctions.map(f => ({
      ...f,
      isFunction: true,
      content: f.description
    })),
    ...messages
  ];

  // Encontrar el mensaje asociado si la acción es 'message'
  const selectedMessage = option.action === 'message' && option.target 
    ? messages.find(m => m.id === option.target) 
    : null;

  return (
    <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
        <Badge variant="secondary" className="font-mono">{option.key}</Badge>
        <Input 
          value={option.label || ''} 
          onChange={(e) => onChange({ ...option, label: e.target.value })}
          placeholder="Texto de la opción"
          className="flex-1"
        />
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <X className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Tecla</Label>
          <Input 
            value={option.key || ''} 
            onChange={(e) => onChange({ ...option, key: e.target.value })}
            placeholder="1, 2, a, b..."
            className="font-mono"
          />
        </div>
        <div>
          <Label className="text-xs">Acción</Label>
          <Select value={option.action || 'submenu'} onValueChange={(v) => onChange({ ...option, action: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {actionTypes.map(at => (
                <SelectItem key={at.value} value={at.value}>
                  <div className="flex items-center gap-2">
                    <at.icon className="h-3 w-3" />
                    {at.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {option.action === 'submenu' && (
        <div>
          <Label className="text-xs">Menú destino</Label>
          <Select value={option.target || ''} onValueChange={(v) => onChange({ ...option, target: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar menú..." />
            </SelectTrigger>
            <SelectContent>
              {allMenus.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {option.action === 'message' && (
        <div className="space-y-2">
          <Label className="text-xs">Mensaje(s) a mostrar</Label>
          <p className="text-xs text-gray-500">Seleccioná uno o más mensajes. Se enviarán en orden.</p>
          
          {/* Mensajes seleccionados */}
          {(option.targets?.length > 0 || option.target) && (
            <div className="flex flex-wrap gap-1 mb-2">
              {(option.targets || (option.target ? [option.target] : [])).map((msgId, idx) => {
                const msg = messages.find(m => m.id === msgId);
                return (
                  <Badge 
                    key={msgId} 
                    variant="secondary" 
                    className="flex items-center gap-1 cursor-pointer hover:bg-red-100"
                    onClick={() => {
                      const currentTargets = option.targets || (option.target ? [option.target] : []);
                      const newTargets = currentTargets.filter(t => t !== msgId);
                      onChange({ ...option, targets: newTargets, target: newTargets[0] || null });
                    }}
                  >
                    <span className="text-xs font-mono text-gray-500">{idx + 1}.</span>
                    {msg?.name || msgId}
                    <X className="h-3 w-3 text-red-500" />
                  </Badge>
                );
              })}
            </div>
          )}
          
          {/* Selector para agregar mensaje o función */}
          <div className="flex gap-2">
            <Select 
              value="" 
              onValueChange={(v) => {
                if (!v) return;
                const currentTargets = option.targets || (option.target ? [option.target] : []);
                if (!currentTargets.includes(v)) {
                  const newTargets = [...currentTargets, v];
                  onChange({ ...option, targets: newTargets, target: newTargets[0] });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="+ Agregar mensaje o función..." />
              </SelectTrigger>
              <SelectContent>
                {/* Funciones primero */}
                {availableFunctions
                  .filter(f => !(option.targets || []).includes(f.id) && f.id !== option.target)
                  .map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      <div className="flex items-center gap-2">
                        <Zap className="h-3 w-3 text-amber-500" />
                        <span>{f.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                {availableFunctions.length > 0 && messages.length > 0 && (
                  <SelectItem value="__separator__" disabled>
                    ─── Mensajes ───
                  </SelectItem>
                )}
                {/* Mensajes normales */}
                {messages
                  .filter(m => !(option.targets || []).includes(m.id) && m.id !== option.target)
                  .map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                      <span className="text-xs text-gray-400 ml-2">({m.category})</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Preview de mensajes seleccionados */}
          {(option.targets?.length > 0 || option.target) && (
            <div className="bg-white border rounded p-2 max-h-48 overflow-auto space-y-2">
              {(option.targets || (option.target ? [option.target] : [])).map((msgId, idx) => {
                // Buscar si es una función o un mensaje
                const func = availableFunctions.find(f => f.id === msgId);
                const msg = messages.find(m => m.id === msgId);
                
                if (func) {
                  return (
                    <div key={msgId} className="border-b pb-2 last:border-b-0 last:pb-0 bg-amber-50 -mx-2 px-2 py-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono bg-amber-100 text-amber-700 px-1 rounded">{idx + 1}/{(option.targets || [option.target]).length}</span>
                        <Zap className="h-3 w-3 text-amber-500" />
                        <span className="text-xs font-medium text-amber-700">{func.name}</span>
                      </div>
                      <p className="text-xs text-amber-600">{func.description}</p>
                    </div>
                  );
                }
                
                if (!msg) return null;
                return (
                  <div key={msgId} className="border-b pb-2 last:border-b-0 last:pb-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono bg-blue-100 text-blue-700 px-1 rounded">{idx + 1}/{(option.targets || [option.target]).length}</span>
                      <span className="text-xs font-medium text-blue-600">{msg.name}</span>
                      {msg.media_url && <Badge variant="outline" className="text-[10px]">📎 Archivo</Badge>}
                    </div>
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans">
                      {msg.content?.substring(0, 150)}{msg.content?.length > 150 ? '...' : ''}
                    </pre>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminBotEditorPage() {
  const { token, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('menus');
  
  // Estados para edición
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingMenuOriginalId, setEditingMenuOriginalId] = useState(null); // Para rastrear el ID original
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingMessageOriginalId, setEditingMessageOriginalId] = useState(null); // Para rastrear el ID original del mensaje
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirmReset, setConfirmReset] = useState(false);
  
  // Estado para selector de archivos existentes
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  // Estado para funciones disponibles
  const [availableFunctions, setAvailableFunctions] = useState([]);

  // Cargar configuración
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, functionsRes] = await Promise.all([
        axios.get(`${API_URL}/webhooks/twilio/admin/bot-config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/webhooks/twilio/admin/bot-config/functions`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })) // Si falla, devolver array vacío
      ]);
      setConfig(configRes.data);
      setAvailableFunctions(functionsRes.data || []);
      setError(null);
    } catch (err) {
      setError('Error al cargar la configuración del bot');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Cargar lista de archivos existentes
  const loadExistingFiles = async () => {
    try {
      setLoadingFiles(true);
      const response = await axios.get(`${API_URL}/webhooks/twilio/admin/bot-config/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingFiles(response.data || []);
    } catch (err) {
      console.error('Error cargando archivos:', err);
      setExistingFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Guardar configuración completa
  const saveConfig = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/webhooks/twilio/admin/bot-config`, 
        { config },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMsg('Configuración guardada correctamente');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Error al guardar la configuración');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Resetear configuración
  const resetConfig = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API_URL}/webhooks/twilio/admin/bot-config/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data.config);
      setConfirmReset(false);
      setSuccessMsg('Configuración reseteada a valores por defecto');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Error al resetear la configuración');
    } finally {
      setSaving(false);
    }
  };

  // Cargar historial
  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/webhooks/twilio/admin/bot-config/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
      setShowHistory(true);
    } catch (err) {
      setError('Error al cargar el historial');
    }
  };

  // Funciones de menús
  const updateMenu = (originalMenuId, updatedMenu) => {
    setConfig(prev => {
      // Buscar el menú por el ID original
      const menuIndex = prev.menus.findIndex(m => m.id === originalMenuId);
      
      if (menuIndex === -1) {
        // Si es un menú nuevo (no existe), agregarlo
        return {
          ...prev,
          menus: [...prev.menus, updatedMenu]
        };
      }
      
      // Reemplazar el menú existente (incluso si cambió el ID)
      const newMenus = [...prev.menus];
      newMenus[menuIndex] = updatedMenu;
      
      return {
        ...prev,
        menus: newMenus
      };
    });
  };

  const addMenu = () => {
    const newId = `menu_${Date.now()}`;
    const newMenu = {
      id: newId,
      name: 'Nuevo Menú',
      title: '📋 *Nuevo Menú*',
      description: '',
      show_back_option: true,
      back_target: 'menu_principal',
      parent_id: 'menu_principal',
      options: []
    };
    setConfig(prev => ({
      ...prev,
      menus: [...prev.menus, newMenu]
    }));
    setEditingMenuOriginalId(newId);
    setEditingMenu(newMenu);
  };

  // Helper para abrir el editor de menú
  const openMenuEditor = (menu) => {
    setEditingMenuOriginalId(menu.id);
    setEditingMenu(menu);
  };

  const deleteMenu = (menuId) => {
    if (menuId === 'menu_principal') return;
    setConfig(prev => ({
      ...prev,
      menus: prev.menus.filter(m => m.id !== menuId)
    }));
  };

  // Funciones de mensajes
  const updateMessage = (originalMsgId, updatedMsg) => {
    setConfig(prev => ({
      ...prev,
      messages: prev.messages.map(m => m.id === originalMsgId ? updatedMsg : m)
    }));
  };

  const addMessage = () => {
    const newId = `msg_${Date.now()}`;
    const newMsg = {
      id: newId,
      name: 'Nuevo Mensaje',
      content: 'Contenido del mensaje...',
      category: 'general'
    };
    setConfig(prev => ({
      ...prev,
      messages: [...prev.messages, newMsg]
    }));
    setEditingMessage(newMsg);
    setEditingMessageOriginalId(newId); // Guardar ID original
  };

  const deleteMessage = (msgId) => {
    setConfig(prev => ({
      ...prev,
      messages: prev.messages.filter(m => m.id !== msgId)
    }));
  };

  // Ejecutar migración de BD
  const runMigration = async () => {
    try {
      const response = await axios.post(`${API_URL}/webhooks/twilio/admin/bot-config/migrate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSuccessMsg(`Migración: ${response.data.message}`);
        if (response.data.changes?.length > 0) {
          console.log('Cambios aplicados:', response.data.changes);
          // Recargar configuración
          await loadConfig();
        }
      }
    } catch (err) {
      setError('Error al ejecutar migración: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0 overflow-x-hidden" data-testid="bot-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
            Editor de Bot WhatsApp
          </h1>
          <p className="text-gray-600 text-xs md:text-sm">Configura los menús, mensajes y comportamiento del bot Ayni</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Botones solo para superadmin */}
          {isSuperAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => {
                // Exportar configuración como JSON
                const exportData = {
                  menus: config?.menus || [],
                  messages: config?.messages || [],
                  keywords: config?.keywords || []
                };
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `bot-config-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }} data-testid="btn-export" className="text-xs md:text-sm text-green-600 border-green-300 hover:bg-green-50">
                <Save className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Exportar</span>
                <span className="md:hidden">↓</span>
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const text = await file.text();
                      const importedConfig = JSON.parse(text);
                      if (importedConfig.menus && importedConfig.messages) {
                        // Reemplazar completamente la configuración
                        setConfig({
                          menus: importedConfig.menus || [],
                          messages: importedConfig.messages || [],
                          keywords: importedConfig.keywords || []
                        });
                        setSuccessMsg('Configuración importada. Hacé clic en "Guardar" para aplicar los cambios.');
                      } else {
                        setError('El archivo no tiene el formato correcto (requiere menus y messages)');
                      }
                    } catch (err) {
                      setError('Error al leer el archivo: ' + err.message);
                    }
                    e.target.value = '';
                  }}
                />
                <Button variant="outline" size="sm" asChild data-testid="btn-import" className="text-xs md:text-sm text-blue-600 border-blue-300 hover:bg-blue-50">
                  <span>
                    <Upload className="h-4 w-4 md:mr-2" />
                    <span className="hidden md:inline">Importar</span>
                    <span className="md:hidden">↑</span>
                  </span>
                </Button>
              </label>
              <Button variant="outline" size="sm" onClick={() => {
                if (window.confirm('⚠️ ¿Estás seguro de BORRAR TODA la configuración del bot?\n\nEsto eliminará todos los menús, mensajes y keywords.\n\nEsta acción requiere guardar para aplicarse.')) {
                  setConfig({
                    menus: [],
                    messages: [],
                    keywords: []
                  });
                  setSuccessMsg('Configuración borrada. Hacé clic en "Guardar" para aplicar o "Importar" para cargar una nueva.');
                }
              }} data-testid="btn-clear" className="text-xs md:text-sm text-red-600 border-red-300 hover:bg-red-50">
                <Trash2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Borrar Bot</span>
                <span className="md:hidden">🗑</span>
              </Button>
              <Button variant="outline" size="sm" onClick={runMigration} data-testid="btn-migrate" className="text-xs md:text-sm text-orange-600 border-orange-300 hover:bg-orange-50">
                <RotateCcw className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Migrar BD</span>
                <span className="md:hidden">Migrar</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirmReset(true)} data-testid="btn-reset" className="text-xs md:text-sm">
                <RotateCcw className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Resetear</span>
              </Button>
            </>
          )}
          {/* Botones para todos los admins */}
          <Button variant="outline" size="sm" onClick={loadHistory} data-testid="btn-history" className="text-xs md:text-sm">
            <History className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Historial</span>
          </Button>
          <Button size="sm" onClick={saveConfig} disabled={saving} data-testid="btn-save" className="text-xs md:text-sm">
            {saving ? <Loader2 className="h-4 w-4 md:mr-2 animate-spin" /> : <Save className="h-4 w-4 md:mr-2" />}
            <span className="hidden md:inline">Guardar Cambios</span>
            <span className="md:hidden">Guardar</span>
          </Button>
        </div>
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="h-4 w-4" />
          {successMsg}
        </div>
      )}

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="menus" className="flex flex-col md:flex-row items-center gap-1 md:gap-2 py-2 px-1 md:px-3 text-xs md:text-sm" data-testid="tab-menus">
            <Menu className="h-4 w-4" />
            <span className="hidden sm:inline">Menús</span>
            <span className="sm:hidden">Menú</span>
            <span className="text-[10px] md:text-xs">({config?.menus?.length || 0})</span>
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex flex-col md:flex-row items-center gap-1 md:gap-2 py-2 px-1 md:px-3 text-xs md:text-sm" data-testid="tab-messages">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Mensajes</span>
            <span className="sm:hidden">Msgs</span>
            <span className="text-[10px] md:text-xs">({config?.messages?.length || 0})</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex flex-col md:flex-row items-center gap-1 md:gap-2 py-2 px-1 md:px-3 text-xs md:text-sm" data-testid="tab-settings">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Configuración</span>
            <span className="sm:hidden">Config</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Menús */}
        <TabsContent value="menus" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg md:text-xl">Estructura de Menús</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Visualiza y edita la estructura jerárquica de menús del bot</CardDescription>
                </div>
                <Button onClick={addMenu} data-testid="btn-add-menu" size="sm" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Menú
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 md:px-6">
              <div className="border rounded-lg p-2 md:p-4 bg-white">
                {config?.menus?.filter(m => !m.parent_id || m.parent_id === null).map(menu => (
                  <MenuTreeItem 
                    key={menu.id} 
                    menu={menu} 
                    allMenus={config.menus}
                    onEdit={openMenuEditor}
                    onDelete={deleteMenu}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Mensajes */}
        <TabsContent value="messages" className="space-y-4">
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-lg md:text-xl">Mensajes del Bot</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Edita los textos de bienvenida, errores y respuestas predefinidas</CardDescription>
                </div>
                <Button onClick={addMessage} data-testid="btn-add-message" size="sm" className="w-full sm:w-auto shrink-0">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Mensaje
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 md:px-6">
              <div className="grid gap-3 md:gap-4">
                {config?.messages?.map(msg => (
                  <div key={msg.id} className="border rounded-lg p-3 md:p-4 hover:bg-gray-50 overflow-hidden">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-1 md:gap-2 mb-2 flex-wrap">
                          <MessageSquare className="h-3 w-3 md:h-4 md:w-4 text-blue-500 shrink-0" />
                          <span className="font-medium text-sm md:text-base truncate max-w-[150px] sm:max-w-none">{msg.name}</span>
                          <Badge variant="outline" className="text-[10px] md:text-xs shrink-0">{msg.category}</Badge>
                          <Badge variant="secondary" className="text-[10px] md:text-xs font-mono hidden sm:inline-flex">{msg.id}</Badge>
                          {msg.media_url && (
                            <Badge className="text-[10px] md:text-xs bg-purple-100 text-purple-700 border-purple-200 shrink-0">
                              📎 Archivo
                            </Badge>
                          )}
                        </div>
                        <pre className="text-xs md:text-sm text-gray-600 bg-gray-100 p-2 rounded whitespace-pre-wrap break-words max-h-24 md:max-h-32 overflow-auto w-full">
                          {msg.content?.substring(0, 150)}{msg.content?.length > 150 ? '...' : ''}
                        </pre>
                        {msg.media_url && (
                          <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                            <Link className="h-3 w-3 shrink-0" />
                            <a href={msg.media_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                              {msg.media_url.split('/').pop()}
                            </a>
                          </div>
                        )}
                        {msg.links && msg.links.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1 md:gap-2">
                            {msg.links.map((link, idx) => (
                              <a 
                                key={idx}
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[10px] md:text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100"
                              >
                                <Link className="h-3 w-3" />
                                {link.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 sm:ml-4 self-end sm:self-start">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingMessage(msg); setEditingMessageOriginalId(msg.id); }} className="h-8">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteMessage(msg.id)} className="h-8">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Configuración */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg md:text-xl">Configuración General</CardTitle>
              <CardDescription className="text-xs md:text-sm">Ajustes generales del comportamiento del bot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 px-3 md:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <Label className="text-sm">Nombre del Bot</Label>
                  <Input 
                    value={config?.settings?.bot_name || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      settings: { ...prev.settings, bot_name: e.target.value }
                    }))}
                    data-testid="input-bot-name"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">Nombre de la Institución</Label>
                  <Input 
                    value={config?.settings?.institution_name || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      settings: { ...prev.settings, institution_name: e.target.value }
                    }))}
                    data-testid="input-institution-name"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">Timeout de Sesión (minutos)</Label>
                  <Input 
                    type="number"
                    value={config?.settings?.session_timeout_minutes || 30}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      settings: { ...prev.settings, session_timeout_minutes: parseInt(e.target.value) }
                    }))}
                    data-testid="input-session-timeout"
                    className="text-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Horarios de Atención</Label>
                <div className="grid grid-cols-5 gap-3">
                  {['lunes', 'martes', 'miercoles', 'jueves', 'viernes'].map(dia => (
                    <div key={dia} className="border rounded-lg p-3 text-center">
                      <span className="font-medium capitalize text-sm block mb-2">{dia}</span>
                      <Input 
                        type="text"
                        placeholder="10:00"
                        value={config?.settings?.horarios_atencion?.[dia]?.inicio || ''}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            horarios_atencion: {
                              ...prev.settings?.horarios_atencion,
                              [dia]: {
                                ...prev.settings?.horarios_atencion?.[dia],
                                inicio: e.target.value
                              }
                            }
                          }
                        }))}
                        className="text-center mb-1 text-sm"
                      />
                      <span className="text-gray-400 text-xs">a</span>
                      <Input 
                        type="text"
                        placeholder="14:00"
                        value={config?.settings?.horarios_atencion?.[dia]?.fin || ''}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            horarios_atencion: {
                              ...prev.settings?.horarios_atencion,
                              [dia]: {
                                ...prev.settings?.horarios_atencion?.[dia],
                                fin: e.target.value
                              }
                            }
                          }
                        }))}
                        className="text-center mt-1 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-gray-500 text-sm">Información del Sistema</Label>
                <div className="mt-2 text-sm text-gray-600">
                  <p>Versión de configuración: <span className="font-mono">{config?.version || '1.0'}</span></p>
                  <p>Última actualización: <span className="font-mono">{config?.updated_at || 'N/A'}</span></p>
                  <p>Actualizado por: <span className="font-mono">{config?.updated_by || 'N/A'}</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Editar Menú */}
      <Dialog open={!!editingMenu} onOpenChange={() => { setEditingMenu(null); setEditingMenuOriginalId(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMenu?.id === 'menu_principal' ? 'Editar Menú Principal' : 'Editar Menú'}
            </DialogTitle>
            <DialogDescription>
              ID: <code className="bg-gray-100 px-1 rounded">{editingMenu?.id}</code>
            </DialogDescription>
          </DialogHeader>

          {editingMenu && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre (interno)</Label>
                  <Input 
                    value={editingMenu.name || ''}
                    onChange={(e) => setEditingMenu({ ...editingMenu, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>ID único</Label>
                  <Input 
                    value={editingMenu.id || ''}
                    onChange={(e) => setEditingMenu({ ...editingMenu, id: e.target.value })}
                    disabled={editingMenu.id === 'menu_principal'}
                    className="font-mono"
                  />
                </div>
              </div>

              <div>
                <Label>Título (se muestra al usuario)</Label>
                <Textarea 
                  value={editingMenu.title || ''}
                  onChange={(e) => setEditingMenu({ ...editingMenu, title: e.target.value })}
                  rows={2}
                  placeholder="Usa *texto* para negrita, _texto_ para cursiva"
                />
              </div>

              <div>
                <Label>Descripción (opcional)</Label>
                <Input 
                  value={editingMenu.description || ''}
                  onChange={(e) => setEditingMenu({ ...editingMenu, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Menú padre</Label>
                  <Select 
                    value={editingMenu.parent_id || 'none'} 
                    onValueChange={(v) => setEditingMenu({ ...editingMenu, parent_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Ninguno (menú raíz)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguno (menú raíz)</SelectItem>
                      {config?.menus?.filter(m => m.id !== editingMenu.id).map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <Switch 
                    checked={editingMenu.show_back_option ?? true}
                    onCheckedChange={(v) => setEditingMenu({ ...editingMenu, show_back_option: v })}
                  />
                  <Label>Mostrar opción "Volver"</Label>
                </div>
              </div>

              <div>
                <Label>Texto final / CTA (opcional)</Label>
                <Input 
                  value={editingMenu.footer || ''}
                  onChange={(e) => setEditingMenu({ ...editingMenu, footer: e.target.value })}
                  placeholder="Ej: Escribí el número de la opción que te interesa."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Texto que aparece al final del menú, después de las opciones. Se muestra en cursiva.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Opciones del Menú</Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingMenu({
                      ...editingMenu,
                      options: [...(editingMenu.options || []), { key: String(editingMenu.options?.length + 1 || 1), label: '', action: 'submenu', target: '' }]
                    })}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Agregar Opción
                  </Button>
                </div>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {editingMenu.options?.map((opt, idx) => (
                    <OptionEditor 
                      key={idx}
                      option={opt}
                      index={idx}
                      allMenus={config?.menus || []}
                      messages={config?.messages || []}
                      availableFunctions={availableFunctions}
                      onChange={(updated) => {
                        const newOpts = [...editingMenu.options];
                        newOpts[idx] = updated;
                        setEditingMenu({ ...editingMenu, options: newOpts });
                      }}
                      onRemove={() => {
                        setEditingMenu({
                          ...editingMenu,
                          options: editingMenu.options.filter((_, i) => i !== idx)
                        });
                      }}
                      onEditMessage={(msg) => {
                        // Cerrar el modal de menú y abrir el de mensaje
                        setEditingMenu(null);
                        setEditingMenuOriginalId(null);
                        setEditingMessage(msg);
                        setEditingMessageOriginalId(msg.id);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingMenu(null); setEditingMenuOriginalId(null); }}>Cancelar</Button>
            <Button onClick={() => {
              if (editingMenu && editingMenuOriginalId) {
                updateMenu(editingMenuOriginalId, editingMenu);
                setEditingMenu(null);
                setEditingMenuOriginalId(null);
              }
            }}>
              <Save className="h-4 w-4 mr-2" />
              Guardar Menú
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Mensaje */}
      <Dialog open={!!editingMessage} onOpenChange={() => { setEditingMessage(null); setEditingMessageOriginalId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Mensaje</DialogTitle>
            <DialogDescription>
              ID: <code className="bg-gray-100 px-1 rounded">{editingMessage?.id}</code>
            </DialogDescription>
          </DialogHeader>

          {editingMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input 
                    value={editingMessage.name || ''}
                    onChange={(e) => setEditingMessage({ ...editingMessage, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select 
                    value={editingMessage.category || 'general'} 
                    onValueChange={(v) => setEditingMessage({ ...editingMessage, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="bienvenida">Bienvenida</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                      <SelectItem value="info">Información</SelectItem>
                      <SelectItem value="ayuda">Ayuda</SelectItem>
                      <SelectItem value="takeover">Takeover</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>ID único</Label>
                <Input 
                  value={editingMessage.id || ''}
                  onChange={(e) => setEditingMessage({ ...editingMessage, id: e.target.value })}
                  className="font-mono"
                />
              </div>

              <div>
                <Label>Contenido del Mensaje</Label>
                <Textarea 
                  value={editingMessage.content || ''}
                  onChange={(e) => setEditingMessage({ ...editingMessage, content: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                  placeholder="Usa *texto* para negrita, _texto_ para cursiva"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formato WhatsApp: *negrita*, _cursiva_, ~tachado~
                </p>
              </div>

              <div>
                <Label>Archivo Adjunto (PDF/Imagen)</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={editingMessage.media_url || ''}
                    onChange={(e) => setEditingMessage({ ...editingMessage, media_url: e.target.value })}
                    placeholder="URL del archivo o elegí uno existente"
                    className="font-mono text-sm flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      loadExistingFiles();
                      setShowFilePicker(true);
                    }}
                    title="Elegir archivo existente"
                  >
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Existentes
                  </Button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Validar tamaño (10MB)
                        if (file.size > 10 * 1024 * 1024) {
                          alert('El archivo es demasiado grande. Máximo 10MB.');
                          return;
                        }
                        
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await axios.post(
                            `${API_URL}/webhooks/twilio/admin/bot-config/upload`,
                            formData,
                            {
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            }
                          );
                          
                          if (response.data.success) {
                            // Construir URL completa
                            const fullUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
                            setEditingMessage({ ...editingMessage, media_url: fullUrl });
                          }
                        } catch (err) {
                          console.error('Error uploading file:', err);
                          alert('Error al subir el archivo: ' + (err.response?.data?.detail || err.message));
                        }
                        
                        // Reset input
                        e.target.value = '';
                      }}
                    />
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-1" />
                        Subir
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Elegí un archivo ya subido, subí uno nuevo (máx 10MB), o pegá una URL externa.
                </p>
                {editingMessage.media_url && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center gap-2">
                      {editingMessage.media_url.endsWith('.pdf') ? (
                        <FileText className="h-4 w-4 text-red-500" />
                      ) : (
                        <Image className="h-4 w-4 text-green-500" />
                      )}
                      <a 
                        href={editingMessage.media_url.startsWith('http') ? editingMessage.media_url : process.env.REACT_APP_BACKEND_URL + editingMessage.media_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm hover:underline truncate flex-1"
                      >
                        {editingMessage.media_url.split('/').pop()}
                      </a>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingMessage({ ...editingMessage, media_url: '' })}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Links Importantes</Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingMessage({
                      ...editingMessage,
                      links: [...(editingMessage.links || []), { label: '', url: '' }]
                    })}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Agregar Link
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Links que se destacan visualmente. También podés incluir links directamente en el contenido del mensaje.
                </p>
                {editingMessage.links && editingMessage.links.length > 0 && (
                  <div className="space-y-2">
                    {editingMessage.links.map((link, idx) => (
                      <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded">
                        <Input 
                          value={link.label || ''}
                          onChange={(e) => {
                            const newLinks = [...editingMessage.links];
                            newLinks[idx] = { ...link, label: e.target.value };
                            setEditingMessage({ ...editingMessage, links: newLinks });
                          }}
                          placeholder="Texto del link"
                          className="flex-1"
                        />
                        <Input 
                          value={link.url || ''}
                          onChange={(e) => {
                            const newLinks = [...editingMessage.links];
                            newLinks[idx] = { ...link, url: e.target.value };
                            setEditingMessage({ ...editingMessage, links: newLinks });
                          }}
                          placeholder="https://..."
                          className="flex-1 font-mono text-sm"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const newLinks = editingMessage.links.filter((_, i) => i !== idx);
                            setEditingMessage({ ...editingMessage, links: newLinks });
                          }}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Editor de Footer */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">Configuración del Footer</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Define qué opciones aparecen al final del mensaje (0 para volver, opciones personalizadas, etc.)
                </p>
                
                <div className="space-y-4 bg-gray-50 p-3 rounded-lg">
                  {/* Opción de volver (0) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="show_back"
                        checked={editingMessage.footer_config?.show_back !== false}
                        onChange={(e) => setEditingMessage({
                          ...editingMessage,
                          footer_config: {
                            ...editingMessage.footer_config,
                            show_back: e.target.checked
                          }
                        })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="show_back" className="text-sm">Mostrar *0* ← Volver</label>
                    </div>
                    {editingMessage.footer_config?.show_back !== false && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Texto:</span>
                        <Input 
                          value={editingMessage.footer_config?.back_label || 'Volver'}
                          onChange={(e) => setEditingMessage({
                            ...editingMessage,
                            footer_config: {
                              ...editingMessage.footer_config,
                              back_label: e.target.value
                            }
                          })}
                          className="w-32 h-8 text-sm"
                          placeholder="Volver"
                        />
                      </div>
                    )}
                  </div>
                  
                  {editingMessage.footer_config?.show_back !== false && (
                    <div className="ml-6">
                      <Label className="text-xs text-gray-600">¿A dónde vuelve?</Label>
                      <Select 
                        value={editingMessage.footer_config?.back_target || '_parent_'} 
                        onValueChange={(v) => setEditingMessage({
                          ...editingMessage,
                          footer_config: {
                            ...editingMessage.footer_config,
                            back_target: v === '_parent_' ? null : v
                          }
                        })}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_parent_">Menú anterior (automático)</SelectItem>
                          <SelectItem value="menu_principal">Menú Principal</SelectItem>
                          {config?.menus?.map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Opción de menú principal (00) */}
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="show_main_menu"
                      checked={editingMessage.footer_config?.show_main_menu === true}
                      onChange={(e) => setEditingMessage({
                        ...editingMessage,
                        footer_config: {
                          ...editingMessage.footer_config,
                          show_main_menu: e.target.checked
                        }
                      })}
                      className="h-4 w-4"
                    />
                    <label htmlFor="show_main_menu" className="text-sm">Mostrar *00* ← Menú principal</label>
                  </div>

                  {/* Opciones personalizadas */}
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm">Opciones Personalizadas</Label>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditingMessage({
                          ...editingMessage,
                          footer_config: {
                            ...editingMessage.footer_config,
                            custom_options: [
                              ...(editingMessage.footer_config?.custom_options || []),
                              { key: '', label: '', action: 'message', target: '' }
                            ]
                          }
                        })}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Agregar Opción
                      </Button>
                    </div>
                    
                    {editingMessage.footer_config?.custom_options?.length > 0 && (
                      <div className="space-y-2">
                        {editingMessage.footer_config.custom_options.map((opt, idx) => (
                          <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded border">
                            <Input 
                              value={opt.key || ''}
                              onChange={(e) => {
                                const newOpts = [...editingMessage.footer_config.custom_options];
                                newOpts[idx] = { ...opt, key: e.target.value };
                                setEditingMessage({
                                  ...editingMessage,
                                  footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                });
                              }}
                              placeholder="Tecla (1, 2, a...)"
                              className="w-20 h-8 text-sm"
                            />
                            <Input 
                              value={opt.label || ''}
                              onChange={(e) => {
                                const newOpts = [...editingMessage.footer_config.custom_options];
                                newOpts[idx] = { ...opt, label: e.target.value };
                                setEditingMessage({
                                  ...editingMessage,
                                  footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                });
                              }}
                              placeholder="Texto de la opción"
                              className="flex-1 h-8 text-sm"
                            />
                            <Select 
                              value={opt.action || 'message'} 
                              onValueChange={(v) => {
                                const newOpts = [...editingMessage.footer_config.custom_options];
                                newOpts[idx] = { ...opt, action: v, targets: v === 'messages' ? [] : undefined };
                                setEditingMessage({
                                  ...editingMessage,
                                  footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                });
                              }}
                            >
                              <SelectTrigger className="w-32 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="message">Mensaje único</SelectItem>
                                <SelectItem value="messages">Mensajes múltiples</SelectItem>
                                <SelectItem value="submenu">Ir a Menú</SelectItem>
                                <SelectItem value="function">Función</SelectItem>
                                <SelectItem value="retry">Reintentar (loop)</SelectItem>
                              </SelectContent>
                            </Select>
                            {opt.action !== 'messages' ? (
                              <Select 
                                value={opt.target || ''} 
                                onValueChange={(v) => {
                                  const newOpts = [...editingMessage.footer_config.custom_options];
                                  newOpts[idx] = { ...opt, target: v };
                                  setEditingMessage({
                                    ...editingMessage,
                                    footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                  });
                                }}
                              >
                                <SelectTrigger className="w-44 h-8 text-sm">
                                  <SelectValue placeholder="Destino..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {opt.action === 'submenu' && config?.menus?.map(m => (
                                    <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                                  ))}
                                  {opt.action === 'message' && config?.messages?.map(m => (
                                    <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                                  ))}
                                  {opt.action === 'function' && (
                                    <>
                                      <SelectItem value="pago_adelantado">Pago Adelantado</SelectItem>
                                      <SelectItem value="bonif_novel">Bonif. Novel</SelectItem>
                                      <SelectItem value="bonif_jubilades">Bonif. Jubilades</SelectItem>
                                      <SelectItem value="bonif_cbt">Bonif. CBT</SelectItem>
                                      <SelectItem value="bonif_cud">Bonif. CUD</SelectItem>
                                      <SelectItem value="bonif_despido">Bonif. Despido</SelectItem>
                                    </>
                                  )}
                                  {opt.action === 'retry' && (
                                    <>
                                      <SelectItem value="opcion_3_deuda_dni">Consulta Deuda (pedir DNI)</SelectItem>
                                      <SelectItem value="registro_dni">Registro (pedir DNI)</SelectItem>
                                      <SelectItem value="confirmar_datos">Confirmar Datos</SelectItem>
                                      <SelectItem value="opcion_1_matriculacion">Matriculación</SelectItem>
                                      <SelectItem value="opcion_2_gestiones">Gestiones</SelectItem>
                                    </>
                                  )}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex-1">
                                <div className="flex flex-wrap gap-1 min-h-[32px] p-1 border rounded bg-white">
                                  {(opt.targets || []).map((targetId, tIdx) => {
                                    const msg = config?.messages?.find(m => m.id === targetId);
                                    return (
                                      <span key={tIdx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                        {msg?.name || targetId}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newTargets = opt.targets.filter((_, i) => i !== tIdx);
                                            const newOpts = [...editingMessage.footer_config.custom_options];
                                            newOpts[idx] = { ...opt, targets: newTargets };
                                            setEditingMessage({
                                              ...editingMessage,
                                              footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                            });
                                          }}
                                          className="hover:text-red-600"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                  <Select
                                    value=""
                                    onValueChange={(v) => {
                                      if (v && !(opt.targets || []).includes(v)) {
                                        const newOpts = [...editingMessage.footer_config.custom_options];
                                        newOpts[idx] = { ...opt, targets: [...(opt.targets || []), v] };
                                        setEditingMessage({
                                          ...editingMessage,
                                          footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                        });
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="w-32 h-6 text-xs border-dashed">
                                      <span className="text-gray-400">+ Agregar</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {config?.messages?.filter(m => !(opt.targets || []).includes(m.id)).map(m => (
                                        <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                const newOpts = editingMessage.footer_config.custom_options.filter((_, i) => i !== idx);
                                setEditingMessage({
                                  ...editingMessage,
                                  footer_config: { ...editingMessage.footer_config, custom_options: newOpts }
                                });
                              }}
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingMessage(null); setEditingMessageOriginalId(null); }}>Cancelar</Button>
            <Button onClick={() => {
              if (editingMessage && editingMessageOriginalId) {
                updateMessage(editingMessageOriginalId, editingMessage);
                setEditingMessage(null);
                setEditingMessageOriginalId(null);
              }
            }}>
              <Save className="h-4 w-4 mr-2" />
              Guardar Mensaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Historial */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de Cambios</DialogTitle>
            <DialogDescription>Últimas 10 versiones guardadas de la configuración</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {history.map((item, idx) => (
              <div key={idx} className="border rounded-lg p-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">Versión {item.config?.version || '1.0'}</span>
                    <span className="text-gray-500 text-sm ml-3">
                      {new Date(item.saved_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </span>
                  </div>
                  <Badge variant="outline">{item.saved_by}</Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {item.config?.menus?.length || 0} menús, {item.config?.messages?.length || 0} mensajes, {item.config?.keywords?.length || 0} keywords
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar Reset */}
      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmar Reset
            </DialogTitle>
            <DialogDescription>
              Esta acción eliminará toda la configuración actual y la reemplazará con los valores por defecto.
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={resetConfig} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Resetear Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Selector de Archivos Existentes */}
      <Dialog open={showFilePicker} onOpenChange={setShowFilePicker}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Archivos Subidos
            </DialogTitle>
            <DialogDescription>
              Seleccioná un archivo ya subido al sistema para adjuntarlo al mensaje.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[50vh]">
            {loadingFiles ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Cargando archivos...</span>
              </div>
            ) : existingFiles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No hay archivos subidos todavía.</p>
                <p className="text-sm mt-1">Subí un archivo desde el campo de arriba.</p>
              </div>
            ) : (
              <div className="grid gap-2">
                {existingFiles.map((file, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => {
                      const fullUrl = process.env.REACT_APP_BACKEND_URL + file.url;
                      setEditingMessage({ ...editingMessage, media_url: fullUrl });
                      setShowFilePicker(false);
                    }}
                  >
                    {file.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <Image className="h-8 w-8 text-green-500 flex-shrink-0" />
                    ) : file.filename.match(/\.pdf$/i) ? (
                      <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-8 w-8 text-blue-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.filename}</p>
                      <p className="text-xs text-gray-500">
                        {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''} 
                        {file.modified && ` • ${new Date(file.modified * 1000).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}`}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="flex-shrink-0">
                      Usar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFilePicker(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
